import React, { useState } from 'react';

// Custom Tabs / deep-link OAuth bridge for the native Android app.
//
// Why: Google blocks OAuth in embedded Android WebViews ("disallowed_useragent"),
// so we cannot use Clerk's in-WebView Google sign-in button. Instead we open
// Chrome Custom Tabs (real Chrome) pointed at kata.fit with a special query
// param. The user signs in via Google there; the PWA mints a single-use Clerk
// sign-in ticket and redirects to a fit.kata.mobile:// deep link. The native
// app captures that deep link and exchanges the ticket for a session inside
// its own WebView (see App.js useEffect that listens for 'appUrlOpen').

export const NATIVE_OAUTH_STATE_KEY = 'nativeOAuthState';
export const NATIVE_OAUTH_PENDING_KEY = 'nativeOAuthPending';

function isNative() {
  return (
    typeof window !== 'undefined' &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform()
  );
}

function generateState() {
  // Browser crypto is available in the Android WebView.
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
}

const NativeGoogleSignIn = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!isNative()) return null;

  const handleClick = async () => {
    setError(null);
    setBusy(true);
    try {
      const state = generateState();
      try { localStorage.setItem(NATIVE_OAUTH_STATE_KEY, state); } catch (_) {}
      try { localStorage.setItem(NATIVE_OAUTH_PENDING_KEY, '1'); } catch (_) {}

      const url = `https://kata.fit/?mobile_oauth=1&state=${encodeURIComponent(state)}`;
      const Browser = window.Capacitor?.Plugins?.Browser;
      if (!Browser) {
        throw new Error('Browser plugin not available.');
      }
      await Browser.open({ url, presentationStyle: 'fullscreen' });
      // The deep-link callback is handled by App.js via App.addListener('appUrlOpen').
    } catch (e) {
      console.error('NativeGoogleSignIn open failed:', e);
      setError(e.message || 'Could not open browser.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginBottom: '1rem', width: '100%' }}>
      <button
        onClick={handleClick}
        disabled={busy}
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          background: '#f4f4f5',
          color: '#18181b',
          border: '1px solid rgba(228, 228, 231, 0.22)',
          borderRadius: '0.5rem',
          fontWeight: 500,
          fontSize: '0.95rem',
          cursor: busy ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontFamily: 'inherit',
        }}
      >
        {/* Google "G" mark */}
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
        </svg>
        {busy ? 'Opening browser…' : 'Continue with Google'}
      </button>
      {error && (
        <div style={{ marginTop: '0.5rem', color: '#fca5a5', fontSize: '0.85rem', textAlign: 'center' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default NativeGoogleSignIn;
